import { FormattedMessage, FormattedPlural } from 'react-intl';

import type { ExtractedFiltersEnabledFragment } from '@xing-com/crate-jobs-graphql';
import { useSearchState } from '@xing-com/crate-jobs-hooks';
import { Headline } from '@xing-com/typography';

import { isVisibleJob, normalizeBoundedTotal } from '../../../helpers';
import { EMPTY_STATE_RADIUS, useSearch } from '../../../hooks';
import { LimitedJobCount } from '../../shared';
import { LazyLoader } from '../lazy-loader';
import { ResultsList } from '../results-list';

import { MoreRecommendations } from './more-recommendations';
import { MoreResultsSkeleton } from './more-results-skeleton';
import * as S from './more-results.styles';

type Props = {
  extractedFilters: (ExtractedFiltersEnabledFragment | null)[];
};

const getExtractedLocationRadius = (
  extractedFilters: Props['extractedFilters']
): number | undefined => {
  const filter = extractedFilters.find(
    (extractedFilter) =>
      extractedFilter?.__typename === 'JobFilterLocation' &&
      extractedFilter.location?.localizationValue
  );

  if (filter?.__typename !== 'JobFilterLocation') {
    return undefined;
  }

  return filter.location?.radius ?? 0;
};

const WiderSearchResults: React.FC<{
  extractedLocationRadius?: number;
}> = ({ extractedLocationRadius }) => {
  const { fetchMore, results, isLoading, searchKey, searchPage } = useSearch({
    isEmptyState: true,
    extractedLocationRadius,
  });
  const normalizedBoundedTotal = normalizeBoundedTotal(results?.boundedTotal);
  const searchResultsTotal = results?.total ?? 0;
  const collectionLength = results?.collection.length ?? 0;

  if (isLoading) return <MoreResultsSkeleton />;
  if (collectionLength === 0) return <MoreRecommendations />;

  const collection = results?.collection.filter(isVisibleJob);
  if (!collection) return null;

  return (
    <S.Container>
      <Headline size="medium" sizeWide="large">
        <FormattedPlural
          value={collectionLength}
          one={
            <FormattedMessage id="JOBS_SEARCH_EMPTY_STATE_WIDER_RESULTS_HEADER.one" />
          }
          other={
            <FormattedMessage
              id="JOBS_SEARCH_EMPTY_STATE_WIDER_RESULTS_HEADER.other"
              values={{
                variable_2: normalizedBoundedTotal ? (
                  <LimitedJobCount
                    count={normalizedBoundedTotal.displayTotal}
                    isAtLeast={normalizedBoundedTotal.isAtLeast}
                  />
                ) : searchResultsTotal > 0 ? (
                  <LimitedJobCount count={searchResultsTotal} />
                ) : null,
              }}
            />
          }
        />
      </Headline>
      <ResultsList
        items={collection}
        type="EMPTY_STATE_SEARCH"
        footer={
          <LazyLoader
            fetchMore={fetchMore}
            initialPage={searchPage}
            key={searchKey}
            results={results}
            totalResults={
              normalizedBoundedTotal?.isAtLeast
                ? undefined
                : normalizedBoundedTotal?.total
            }
          />
        }
      />
    </S.Container>
  );
};

export const MoreResults: React.FC<Props> = ({ extractedFilters }) => {
  const { searchQuery } = useSearchState();
  const extractedLocationRadius = getExtractedLocationRadius(extractedFilters);
  const radius = searchQuery.location?.radius ?? extractedLocationRadius;

  if (typeof radius === 'number' && radius >= EMPTY_STATE_RADIUS) {
    return <MoreRecommendations />;
  }

  return (
    <WiderSearchResults extractedLocationRadius={extractedLocationRadius} />
  );
};
